import React, { useEffect, useState } from 'react'

import { useLocation } from 'react-router-dom'

import { getOnePublic } from 'redux/api/Metrics'
import { TPublicMetric } from 'types/GlobalMetric'

import { getLabels, getValues } from 'utils/metric'

import Box from 'components_new/atoms/Box'

import BarChart from 'components_new/molecules/charts/BarChart'

import GenericWidget from 'components_new/organisms/GenericWidget'
import Error from 'components_new/organisms/MetricPreview/error'

import { useLanguage } from 'language/translator'
import { useParams } from 'utils/hooks'

/**
 * Public, tokenless metric preview the website embeds in an iframe:
 * /public/metrics/:kpi_template_id — demo data, no login and no database
 * row tied to the embed. `?language=en` renders the English title/labels.
 */
const PublicMetricPage = () => {
  const params = useParams<{ id: string }>()
  const location = useLocation()
  const { lang, setLang } = useLanguage()

  const [metric, setMetric] = useState<TPublicMetric | null>(null)
  const [error, setError] = useState(false)

  const languageParam = new URLSearchParams(location.search).get('language')
  const desiredLang =
    languageParam === 'en' || languageParam === 'sv' ? languageParam : null

  // Depends on lang: LanguageProvider resets to 'sv' on mount after this
  // effect has run, so re-apply until the requested language sticks.
  useEffect(() => {
    if (desiredLang && lang !== desiredLang) {
      setLang(desiredLang)
    }
  }, [desiredLang, lang])

  useEffect(() => {
    getOnePublic(params.id)
      .then((response) => setMetric(response.data.data))
      .catch(() => setError(true))
  }, [])

  if (error) {
    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          height: '100vh',
          justifyContent: 'center',
          width: '100vw'
        }}
      >
        <Error />
      </Box>
    )
  }

  const title = metric ? metric.title[lang] : null

  // The public endpoint always segments by year_month, so the default
  // date labels apply.
  const labels = getLabels({ lang, rawData: metric, segmentOption: null })
  const values = getValues({ rawData: metric })

  return (
    <Box sx={{ height: '100vh', width: '100vw' }}>
      <GenericWidget
        disableFullsize={true}
        disableSubtitle={!metric?.unit}
        editMode={false}
        loading={!metric}
        subtitle={metric?.unit ?? null}
        title={title}
      >
        <BarChart
          data={{
            datasets: [
              {
                label: title ?? '',
                main: true,
                type: 'bar',
                unit: metric?.unit ?? null,
                values: values
              }
            ],
            labels: labels
          }}
          flexItem={true}
          variant="detailed"
        />
      </GenericWidget>
    </Box>
  )
}

export default PublicMetricPage
